import {
  autoUpdate,
  flip,
  offset,
  Placement,
  shift,
  useFloating,
} from '@floating-ui/react-dom';
import clsx from 'clsx';
import React, { useLayoutEffect, useState } from 'react';

export interface SmartTooltipProps {
  /** Optional title that appears in bold at the top of the tooltip. */
  title?: string;
  /** The main text content of the tooltip. */
  content?: string | React.ReactNode;
  /** The element that triggers the tooltip on hover. */
  children: React.ReactNode;
  /**
   * Preferred placement for the tooltip. If there's not enough space,
   * it will flip to another side automatically.
   * @default 'right-end'
   */
  placement?: Placement;
  /**
   * External control for tooltip visibility.
   * When set to true or false, it will override the hover state.
   * When undefined, the tooltip will use hover behavior.
   */
  isOpen?: boolean;
  /**
   * Disables hover functionality when set to true.
   * Useful when you want to control the tooltip only through the isOpen prop.
   * @default false
   */
  disableHover?: boolean;
  icon?: JSX.Element;
  titleClassname?: string;
}

export const SmartTooltip: React.FC<SmartTooltipProps> = ({
  title,
  content,
  children,
  placement = 'right-end',
  isOpen,
  disableHover = false,
  icon,
  titleClassname,
}) => {
  // Tooltip visibility - internal state for hover
  const [isHovering, setIsHovering] = useState(false);

  // Determine if tooltip should be open based on props and hover state
  const open = isOpen !== undefined ? isOpen : !disableHover && isHovering;

  // Floating UI hook to manage positioning
  const { x, y, strategy, refs, update } = useFloating({
    placement,
    middleware: [
      offset(10), // Gap (in pixels) between the tooltip and the trigger element
      flip(), // Flips the tooltip if there's insufficient space
      shift({
        padding: 5, // Ensures a bit of padding from the edges of the screen
      }),
    ],
  });

  // Auto-update the tooltip position if anything changes (e.g., window resize)
  useLayoutEffect(() => {
    if (!open || !refs.reference.current || !refs.floating.current) return;

    const cleanup = autoUpdate(
      refs.reference.current,
      refs.floating.current,
      update
    );

    return cleanup;
  }, [open, refs.reference, refs.floating, update]);

  // Handlers to control tooltip hover visibility
  const showTooltip = () => {
    if (!disableHover) {
      setIsHovering(true);
    }
  };

  const hideTooltip = () => {
    if (!disableHover) {
      setIsHovering(false);
    }
  };

  return (
    <div
      ref={refs.setReference}
      onMouseEnter={showTooltip}
      onMouseLeave={hideTooltip}
      className="inline-block"
    >
      {/* This is the trigger element (your box or bounding box) */}
      {children}

      {open && (
        <div
          ref={refs.setFloating}
          style={{
            position: strategy,
            top: y ?? 0,
            left: x ?? 0,
          }}
          className="dark:bg-ondark-bg-2 bg-onlight-bg-2 dark:border-ondark-line-2 z-50 max-w-xs rounded-md border p-2 shadow-lg"
        >
          {/* Title (optional) */}
          {title && (
            <div className="mb-1 flex gap-2 text-sm font-bold">
              {icon}
              <span className={clsx(titleClassname ?? '')}>{title}</span>
            </div>
          )}

          {/* Content (main text) */}
          {content && <div className="text-xs">{content}</div>}
        </div>
      )}
    </div>
  );
};
